import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import {
  Heart, Play, CheckCircle2, Star, X, ArrowRight, Sparkles, Trophy, MessageCircle,
} from "lucide-react";
import { myApi } from "../api";
import { verifyEmail } from "@/shared/emailValidation";

const inputCls =
  "w-full px-4 py-2.5 border border-[#E6E1D6] rounded-[14px] text-sm text-[#0F1829] focus:outline-none focus:ring-2 focus:ring-[#978D78] bg-white transition";

function Toast({ msg, onClose }) {
  if (!msg) return null;
  return (
    <div className={`mb-5 px-4 py-2.5 rounded-[14px] text-sm flex items-center gap-2 ${
      msg.type === "success"
        ? "bg-[#F0F5E8] text-[#4A5E2E] border border-[#6F7559]/30"
        : "bg-red-50 text-[#B91C1C] border border-red-200"
    }`}>
      <CheckCircle2 size={16} /> {msg.text}
      <button onClick={onClose} className="ml-auto opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

export default function MyMudarDeVida() {
  const [status, setStatus] = useState(null);
  const [desafios, setDesafios] = useState([]);
  const [finais, setFinais] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedDesafio, setSelectedDesafio] = useState(null);

  const loadAll = async () => {
    try {
      const [s, d, f, r] = await Promise.all([
        myApi.mvStatus(), myApi.mvDesafios(), myApi.mvOpcoesFinal(), myApi.mvResultados(),
      ]);
      setStatus(s); setDesafios(d); setFinais(f); setResultados(r);
    } catch (e) { setMsg({ type: "error", text: e.message || "Erro ao carregar" }); }
  };

  useEffect(() => { loadAll(); }, []);

  const subscribe = async (idDesafio = 1) => {
    setBusy(true);
    try {
      await myApi.mvSubscribe(idDesafio);
      setMsg({ type: "success", text: "Inscreveu-se no programa MUDAR DE VIDA." });
      await loadAll();
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setBusy(false); }
  };

  const unsubscribe = async () => {
    if (!window.confirm("Quer mesmo sair do programa MUDAR DE VIDA?")) return;
    setBusy(true);
    try {
      await myApi.mvUnsubscribe();
      setMsg({ type: "success", text: "Saiu do programa." });
      await loadAll();
    } catch (e) { setMsg({ type: "error", text: e.message }); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="text-sm text-[#978D78]">A carregar...</div>;

  const { subscribed, current, total_semanas } = status;
  const currentOrdem = current?.Ordem || 1;

  return (
    <div data-testid="my-mv-page" className="space-y-6">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5F2EB] text-[10px] uppercase tracking-[0.25em] text-[#978D78] mb-2">
          <Sparkles size={12} /> Programa
        </div>
        <h1 className="text-4xl text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          MUDAR DE VIDA
        </h1>
        <p className="text-sm text-[#6B7280] max-w-3xl">
          30 desafios semanais para transformar o seu pensamento e ação. Cada semana recebe, por email,
          um novo desafio com ferramentas práticas para o aplicar. Registe os seus resultados e acompanhe
          a sua evolução.
        </p>
      </div>

      <Toast msg={msg} onClose={() => setMsg(null)} />

      {/* Hero status card */}
      <div className="relative overflow-hidden rounded-[24px] bg-gradient-to-br from-[#0F1829] via-[#1a2542] to-[#6F4F1D] text-white p-8">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 85% 15%, rgba(255,255,255,0.25) 0%, transparent 50%)" }} />
        <div className="relative">
          {subscribed && current ? (
            <>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">Semana actual</div>
              <div className="flex items-baseline gap-3 mb-3">
                <div className="text-5xl" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
                  {currentOrdem}
                </div>
                <div className="text-sm text-white/60">de {total_semanas}</div>
              </div>
              <h3 className="text-xl mb-4 max-w-2xl" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
                {current.Descricao}
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { setSelectedDesafio(current); setFormOpen(true); }}
                  data-testid="mv-open-result-form"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#0F1829] text-sm rounded-full hover:bg-[#D1CFC9] transition font-medium"
                >
                  <MessageCircle size={15} /> Registar resultados desta semana
                </button>
                <button
                  onClick={unsubscribe} disabled={busy}
                  data-testid="mv-unsubscribe-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 text-white/80 text-sm rounded-full hover:bg-white/10 transition"
                >
                  <X size={14} /> Sair do programa
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">Ainda não está inscrito</div>
              <h3 className="text-3xl mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
                Dê o primeiro passo para mudar de vida.
              </h3>
              <p className="text-white/70 text-sm max-w-2xl mb-5">
                Vai receber semanalmente no seu email um desafio para aplicar na sua vida. No final
                de cada semana poderá registar os seus resultados e partilhar o seu testemunho.
              </p>
              <button
                onClick={() => subscribe(1)} disabled={busy}
                data-testid="mv-subscribe-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0F1829] text-sm rounded-full hover:bg-[#D1CFC9] transition font-medium shadow-[0_8px_24px_rgba(151,141,120,0.35)]"
              >
                <Play size={15} /> Começar o programa agora
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content of current challenge */}
      {subscribed && current?.TextoEmail && (
        <div className="bg-white rounded-[20px] border border-[#E6E1D6] p-6 lg:p-8" data-testid="mv-current-content">
          <div className="flex items-center gap-2 mb-4">
            <Star size={18} className="text-[#6F4F1D]" />
            <h2 className="text-xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
              O seu desafio desta semana
            </h2>
          </div>
          <div
            className="mv-email prose prose-sm max-w-none text-[#4B5563]"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(current.TextoEmail || "", { USE_PROFILES: { html: true } })
            }}
          />
        </div>
      )}

      {/* All challenges overview */}
      <div className="bg-white rounded-[20px] border border-[#E6E1D6] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#F5F2EB] flex items-center justify-between">
          <h2 className="text-lg text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            Os 30 Desafios
          </h2>
          <span className="text-xs text-[#978D78]">{desafios.length} semanas</span>
        </div>
        <div className="grid md:grid-cols-2 p-4 gap-2">
          {desafios.map((d) => {
            const done = d.Ordem < currentOrdem;
            const isCurrent = d.Ordem === currentOrdem && subscribed;
            let rowCls = "bg-[#FAF8F3]";
            if (isCurrent) rowCls = "bg-[#0F1829] text-white";
            else if (done) rowCls = "bg-[#F0F5E8]";
            let badgeCls = "bg-white text-[#978D78] border border-[#E6E1D6]";
            if (isCurrent) badgeCls = "bg-white text-[#0F1829]";
            else if (done) badgeCls = "bg-[#4A5E2E] text-white";
            return (
              <div
                key={d.ID_Desafio}
                data-testid={`mv-desafio-${d.ID_Desafio}`}
                className={`flex items-center gap-3 p-3 rounded-[14px] ${rowCls}`}
              >
                <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-semibold ${badgeCls}`}>
                  {done ? <CheckCircle2 size={14} /> : d.Ordem}
                </div>
                <div className="text-sm flex-1 min-w-0">
                  <div className={`truncate ${isCurrent ? "text-white" : "text-[#0F1829]"}`}>
                    {d.Descricao}
                  </div>
                </div>
                {isCurrent && <span className="text-[10px] uppercase tracking-wider bg-white/20 text-white px-2 py-0.5 rounded-full">Actual</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Previous results */}
      {resultados.length > 0 && (
        <div className="bg-white rounded-[20px] border border-[#E6E1D6] overflow-hidden" data-testid="mv-previous-results">
          <div className="px-6 py-4 border-b border-[#F5F2EB]">
            <h2 className="text-lg text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
              Os meus testemunhos ({resultados.length})
            </h2>
          </div>
          <div className="divide-y divide-[#F5F2EB]">
            {resultados.map((r) => (
              <div key={r.ID_DesafioResultado} data-testid={`mv-result-${r.ID_DesafioResultado}`} className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full bg-[#F5F2EB] text-[#978D78] grid place-items-center text-xs font-semibold">
                    {r.Semana}
                  </div>
                  <div className="text-sm text-[#0F1829]">{r.Desafio}</div>
                  <span className="ml-auto text-xs text-[#978D78]">{(r.Data || "").slice(0, 10)}</span>
                </div>
                {r.ResultadosObtidos && (
                  <div className="text-sm text-[#4B5563] pl-9 line-clamp-3">{r.ResultadosObtidos}</div>
                )}
                {r.FinalSemana && (
                  <div className="mt-2 pl-9 inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#F5F2EB] text-[#4B5563]">
                    <Trophy size={10} className="inline mr-1" /> {r.FinalSemana}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Result submission dialog */}
      {formOpen && selectedDesafio && (
        <ResultadoForm
          desafio={selectedDesafio}
          opcoesFinal={finais}
          onClose={() => setFormOpen(false)}
          onSubmitted={async () => {
            setFormOpen(false);
            setMsg({ type: "success", text: "Testemunho registado. Avançou para o desafio seguinte!" });
            await loadAll();
          }}
        />
      )}
    </div>
  );
}

// ─────────────── Resultado Form Dialog ───────────────
function ResultadoForm({ desafio, opcoesFinal, onClose, onSubmitted }) {
  const [f, setF] = useState({
    ID_Desafio: desafio.ID_Desafio,
    IniciativasTomadas: "", ExperienciasVividas: "",
    DificuldadesEncontradas: "", ResultadosObtidos: "",
    ID_DesafioFinalSemana: null,
    ValeAPena: "S", ApareceAnonimo: "N",
    Titulo: "", Nome: "", Organizacao: "", Email: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if ((f.Email || "").trim()) {
      const ev = await verifyEmail(f.Email);
      if (!ev.valid) {
        setErr(ev.suggestion ? `${ev.message} Quis dizer ${ev.suggestion}?` : ev.message);
        return;
      }
    }
    setBusy(true); setErr(null);
    try {
      await myApi.mvSubmitResultado(f);
      onSubmitted();
    } catch (e) {
      setErr(e.message || "Erro ao submeter");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="mv-result-dialog" className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-[24px] w-full max-w-2xl my-8 p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#978D78] mb-1">Semana {desafio.Ordem}</div>
            <h3 className="text-2xl text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
              {desafio.Descricao}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 text-[#978D78] hover:text-[#0F1829]"><X size={20} /></button>
        </div>

        {err && <div className="mb-4 p-3 rounded-[14px] bg-red-50 text-[#B91C1C] text-sm border border-red-200">{err}</div>}

        <div className="space-y-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] font-medium mb-1.5 block">Iniciativas Tomadas</span>
            <textarea rows={3} value={f.IniciativasTomadas} onChange={(e) => setF({ ...f, IniciativasTomadas: e.target.value })} className={inputCls} data-testid="mv-f-iniciativas" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] font-medium mb-1.5 block">Experiências Vividas</span>
            <textarea rows={3} value={f.ExperienciasVividas} onChange={(e) => setF({ ...f, ExperienciasVividas: e.target.value })} className={inputCls} data-testid="mv-f-experiencias" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] font-medium mb-1.5 block">Dificuldades Encontradas</span>
            <textarea rows={3} value={f.DificuldadesEncontradas} onChange={(e) => setF({ ...f, DificuldadesEncontradas: e.target.value })} className={inputCls} data-testid="mv-f-dificuldades" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] font-medium mb-1.5 block">Resultados Obtidos</span>
            <textarea rows={3} value={f.ResultadosObtidos} onChange={(e) => setF({ ...f, ResultadosObtidos: e.target.value })} className={inputCls} data-testid="mv-f-resultados" />
          </label>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] font-medium mb-1.5 block">Como se sente no final da semana?</span>
              <select value={f.ID_DesafioFinalSemana || ""} onChange={(e) => setF({ ...f, ID_DesafioFinalSemana: e.target.value ? Number(e.target.value) : null })} className={inputCls} data-testid="mv-f-final">
                <option value="">—</option>
                {opcoesFinal.map((o) => <option key={o.ID_DesafioFinalSemana} value={o.ID_DesafioFinalSemana}>{o.Descricao}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#978D78] font-medium mb-1.5 block">Vale a pena?</span>
              <select value={f.ValeAPena} onChange={(e) => setF({ ...f, ValeAPena: e.target.value })} className={inputCls} data-testid="mv-f-valeapena">
                <option value="S">Sim, totalmente</option>
                <option value="N">Não</option>
              </select>
            </label>
          </div>

          <div className="bg-[#FAF8F3] rounded-[14px] p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm text-[#4B5563]">
              <input type="checkbox" checked={f.ApareceAnonimo === "S"} onChange={(e) => setF({ ...f, ApareceAnonimo: e.target.checked ? "S" : "N" })} data-testid="mv-f-anonimo" />
              Partilhar anonimamente
            </label>
            {f.ApareceAnonimo === "N" && (
              <div className="grid md:grid-cols-2 gap-3">
                <input placeholder="Título (ex. Dr., Eng.)" value={f.Titulo} onChange={(e) => setF({ ...f, Titulo: e.target.value })} className={inputCls} />
                <input placeholder="Nome" value={f.Nome} onChange={(e) => setF({ ...f, Nome: e.target.value })} className={inputCls} />
                <input placeholder="Organização" value={f.Organizacao} onChange={(e) => setF({ ...f, Organizacao: e.target.value })} className={inputCls} />
                <input placeholder="Email" type="email" value={f.Email} onChange={(e) => setF({ ...f, Email: e.target.value })} className={inputCls} />
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-full border border-[#E6E1D6] text-[#4B5563] hover:bg-[#F5F2EB]">
            Cancelar
          </button>
          <button onClick={submit} disabled={busy} data-testid="mv-f-submit"
            className="inline-flex items-center gap-2 px-5 py-2 text-sm rounded-full bg-[#0F1829] text-white hover:bg-[#1a2542] disabled:opacity-50">
            <ArrowRight size={15} /> {busy ? "A submeter..." : "Submeter testemunho"}
          </button>
        </div>
      </div>
    </div>
  );
}
